import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router";
import _ from "lodash";
import moment, { utc } from "moment-timezone";
import {
  Badge,
  ButtonBase,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
// DesktopDatePicker (not the responsive DatePicker) so touch devices get the
// same close-on-select popper as desktop. The plain DatePicker swaps to a
// mobile modal on iOS that requires a Cancel/OK confirm before it closes.
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickerDay } from "@mui/x-date-pickers";
import ContentWrapper from "../Layout/ContentWrapper";
import {
  getGroupStageGamesAction,
  getKOGamesAction,
} from "../../store/reducers/games";
import Spinner from "../Layout/Spinner";
// import { getDateRange } from "../../hooks/getDateRange";
import { testGroupStageGames } from "../../assets/testData";
import { getCountryInfo } from "../../utils/countryUtils";
import { useContestId } from "../../hooks/useContestId";

// import moment from "moment-timezone";
// import { useSelector } from "react-redux";

const Games = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXsMobile = useMediaQuery(theme.breakpoints.down(500));
  const dispatch = useDispatch();
  const currDate = moment.utc();

  const startDate = 1781204400;
  const koStartDate = 1782673200;
  const endDate = 1784487600;

  // A Knockout contest has no group stage of its own. To let entrants follow
  // who's advancing — and watch live group scores while the bracket waits — the
  // Games page pulls the real group-stage fixtures from the companion full
  // World Cup contest and shows them as non-scoring reference games.
  // (Single source for now; swap to a /status-provided id if contests pair up
  // differently later.)
  const WC_FULL_CONTEST_ID = 44;

  const groupGames = useSelector((state) => state.games?.groupGames);
  const knockoutGames = useSelector((state) => state.games?.knockoutGames);
  const gamesLoading = useSelector((state) => state.games?.loading);
  const timezone = useSelector((state) => state.app?.tz);
  const contestType = useSelector((state) => state.app?.contestType);
  const isKnockout = contestType === "Knockout";

  const [currStage, setCurrStage] = useState("group");
  const [selectedDate, setSelectedDate] = useState(currDate);
  const [selectedDateProps, setSelectedDateProps] = useState({
    date: "",
    weekday: "",
    gamesQty: 0,
  });
  const [datesArr, setDatesArr] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [selectedDateGames, setSelectedDateGames] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const contestId = useContestId();

  // Pin the heading + date picker beneath the frozen Nav so they stay reachable
  // while scrolling the day's games. Nav height is dynamic, so measure it live.
  const [navOffset, setNavOffset] = useState(0);
  useEffect(() => {
    const nav = document.getElementById("app-nav");
    if (!nav) return;
    const update = () => setNavOffset(nav.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);

  // const getDateRange = useCallback((startDate, endDate) => {
  //   let datesArr = [];
  //   let fromDate = moment.unix(startDate);
  //   let toDate = moment.unix(endDate);

  //   //Logic for getting rest of the dates between two dates("FromDate" to "EndDate")
  //   while (fromDate < toDate) {
  //     const newDate = fromDate.add(1, "days");
  //     const formattedFromDate = fromDate.format("X");
  //     const formattedNewDate = newDate.format("X");

  //     datesArr.push({
  //       dateStart: parseInt(formattedFromDate),
  //       nextDateStart: parseInt(formattedNewDate),
  //       weekday: fromDate?.format("ddd"),
  //       date: fromDate?.format("D"),
  //       month: fromDate?.format("MMM"),
  //     });

  //     fromDate = newDate;
  //   }

  //   return datesArr;
  // }, []);

  // const calDates = getDateRange(startDate, endDate);

  useEffect(() => {
    // On a Knockout contest, source the group fixtures from the companion full
    // contest so they appear as reference games; otherwise use this contest.
    dispatch(
      getGroupStageGamesAction({
        contestId: isKnockout ? WC_FULL_CONTEST_ID : contestId,
      }),
    );
    dispatch(getKOGamesAction({ contestId }));
  }, [dispatch, contestId, isKnockout]);

  useEffect(() => {
    const raw = searchParams.get("matchdate");
    if (!raw) return;
    const dateParam = parseInt(raw, 10);
    if (!Number.isFinite(dateParam)) return;
    // tz can be empty on first paint; moment-timezone's .tz(undefined) acts as a
    // getter and returns undefined (not a moment), which silently dropped the
    // matchdate so the body stayed on the wrong day. Fall back to a guessed zone.
    const tz = timezone || moment.tz.guess();
    const next = moment(dateParam).tz(tz);
    if (next.isValid()) setSelectedDate(next);
  }, [searchParams, timezone]);

  useEffect(() => {
    // Group games arrive nested (team_1: {country_name,…}); knockout games
    // arrive flat (team_1_name, team_1_flag,…). Normalize both to the nested
    // shape the day cards render, reading whichever the payload provides.
    const toNested = (g, stage) => {
      const team = (n) => {
        const nested = g[`team_${n}`];
        return {
          team_number: nested?.team_number ?? g[`team_${n}_number`],
          country_name: nested?.country_name ?? g[`team_${n}_name`],
          flag_image_url: nested?.flag_image_url ?? g[`team_${n}_flag`],
          world_rank: nested?.world_rank ?? g[`team_${n}_world_rank`],
          goals: nested?.goals ?? g[`team_${n}_goals`],
        };
      };
      return {
        ...g,
        wc_stage: stage,
        // Group fixtures on a Knockout contest come from the companion full
        // contest — shown for context, not scored here.
        is_reference: isKnockout && stage === "group",
        group: g.group ?? g.group_name ?? null,
        round_name: g.round_name ?? null,
        team_1: team(1),
        team_2: team(2),
      };
    };

    const groupGamesArr = _.flatten(_.toArray(groupGames)).map((g) =>
      toNested(g, "group"),
    );
    const koGamesArr = _.flatten(_.toArray(knockoutGames)).map((g) =>
      toNested(g, "knockout"),
    );

    const hasTeams = (g) =>
      !!g?.team_1?.country_name && !!g?.team_2?.country_name;

    // Knockout fixtures are seeded before their teams are known (R16+ carry
    // team_number -1 until the prior round finishes). Keep those scheduled
    // matches — with a valid kickoff — so the calendar shows the full bracket
    // through the final; the card renders a TBD placeholder until teams
    // resolve. Group reference games (pulled from the companion full contest)
    // still require real teams, since an unknown group fixture has nothing
    // useful to show.
    const keep = (g) =>
      g?.wc_stage === "knockout"
        ? moment.utc(g?.match_datetime_utc).isValid()
        : hasTeams(g);

    setAllGames([...groupGamesArr, ...koGamesArr].filter(keep));
  }, [groupGames, knockoutGames, isKnockout]);

  // When no explicit ?matchdate is requested, land on the nearest match day
  // (next upcoming, else most recent) rather than today — today is usually an
  // off day, which otherwise left the page stuck on "OFF DAY" with all the
  // games sitting elsewhere on the calendar.
  useEffect(() => {
    if (searchParams.get("matchdate")) return;
    if (_.isEmpty(allGames)) return;
    const tz = timezone || moment.tz.guess();
    const todayKey = moment().tz(tz).format("YYYY-MM-DD");
    const dayKeys = _.uniq(
      allGames.map((g) =>
        moment.utc(g?.match_datetime_utc).tz(tz).format("YYYY-MM-DD"),
      ),
    ).sort();
    const target =
      dayKeys.find((k) => k >= todayKey) || dayKeys[dayKeys.length - 1];
    if (target) setSelectedDate(moment.tz(target, tz));
  }, [allGames, timezone, searchParams]);

  useEffect(() => {
    const tz = timezone || moment.tz.guess();
    if (!_.isEmpty(allGames) && !!selectedDate) {
      // Match on the calendar day in the user's tz, NOT a rolling 24h window
      // from the selected instant. The old window started at the selectedDate
      // moment, so a matchdate carrying a specific kickoff time hid every game
      // earlier that same day — which rendered as "OFF DAY".
      const selKey = moment(selectedDate).tz(tz).format("YYYY-MM-DD");
      const gamesOnDate = allGames.filter(
        (game) =>
          moment.utc(game?.match_datetime_utc).tz(tz).format("YYYY-MM-DD") ===
          selKey,
      );
      // sort chronologically (signed diff; a unary comparator leaves the day
      // unsorted, e.g. 10:00, 7:00, 1:00, 4:00).
      const sortedGames = gamesOnDate.sort(
        (a, b) =>
          Date.parse(a?.match_datetime_utc) - Date.parse(b?.match_datetime_utc),
      );
      setSelectedDateGames(sortedGames);
    } else {
      setSelectedDateGames([]);
    }
  }, [allGames, selectedDate, timezone]);

  useEffect(() => {
    if (!!selectedDate) {
      const updatedProps = {
        weekday: moment(selectedDate)?.format("ddd"),
        date: moment(selectedDate)?.format("D"),
        gamesQty: selectedDateGames?.length || 0,
      };

      setSelectedDateProps(updatedProps);
    }
  }, [selectedDate, selectedDateGames]);

  // useEffect(() => {
  //   // if (!!groupGames && !!knockoutGames) {

  //   console.log("cal dates: ", calDates);

  //   if (!_.isEmpty(calDates) && !_.isEmpty(groupGamesArr)) {
  //     const mappedGames = calDates.map((date) => {
  //       const gGames = groupGamesArr?.filter((game) => {
  //         const utcGameTime = moment.utc(game?.match_datetime_utc).format("X");

  //         return date?.dateStart < utcGameTime < date?.nextDateStart;
  //       });

  //       return {
  //         ...date,
  //         games: gGames,
  //         gamesQty: gGames.length,
  //       };
  //     });

  //     setDatesArr(mappedGames);
  //   }
  //   // }
  // }, [calDates]);

  // useEffect(() => {
  //   console.log("Dates Array: ", datesArr);
  // }, [datesArr]);

  // const datesArr = [
  //   {
  //     dateStart: 0,
  //     nextDateStart: 0,
  //     weekday: "MON",
  //     date: 10,
  //     month: "JAN",
  //     gamesQty: 3,
  //     games: [],
  //   },
  // ];

  const getScheduledTime = useCallback(
    (game) =>
      moment.utc(game?.match_datetime_utc).tz(timezone).format("h:mm A"),
    [timezone],
  );

  // Day-keys (in the user's tz) that have at least one game, derived from the
  // same games the body renders. Lets the calendar mark match days so the user
  // can jump straight to a day of games / results.
  const gameDayKeys = useMemo(() => {
    const tz = timezone || moment.tz.guess();
    const keys = new Set();
    (allGames || []).forEach((g) => {
      const d = moment.utc(g?.match_datetime_utc).tz(tz);
      if (d.isValid()) keys.add(d.format("YYYY-MM-DD"));
    });
    return keys;
  }, [allGames, timezone]);

  // Calendar day cell: a match day gets a cyan dot beneath the date. Selecting
  // any day drives the body to that day's games/results via selectedDate; the
  // dot just signals which days are worth clicking.
  const GameDay = useCallback(
    (dayProps) => {
      const { day, outsideCurrentMonth, selected } = dayProps;
      const hasGames =
        !outsideCurrentMonth &&
        gameDayKeys.has(moment(day).format("YYYY-MM-DD"));
      return (
        <Badge
          key={moment(day).format("YYYY-MM-DD")}
          overlap="circular"
          variant="dot"
          invisible={!hasGames}
          sx={{
            "& .MuiBadge-badge": {
              // Match-day marker beneath the date. The selected day's cell is
              // filled cyan, so a cyan dot there would vanish — render it white
              // on the selected day and cyan on every other match day.
              backgroundColor: selected
                ? theme.branding.gray[0]
                : theme.branding.cyan[500],
              minWidth: "6px",
              height: "6px",
              borderRadius: "50%",
              top: "30px",
              left: "50%",
              right: "auto",
              transform: "translateX(-50%)",
            },
          }}
        >
          <PickerDay {...dayProps} />
        </Badge>
      );
    },
    [gameDayKeys, theme],
  );

  return (
    <>
      {gamesLoading ? (
        // <Spinner show={gamesLoading} />
        <></>
      ) : (
        <ContentWrapper>
          <Grid
            container
            direction="column"
            sx={{ gap: "24px", width: "100%" }}
          >
            {/* header — sticky beneath the frozen Nav */}
            <Grid
              container
              direction={isXsMobile ? "column" : "row"}
              wrap="nowrap"
              sx={[
                {
                  position: "sticky",
                  top: `${navOffset}px`,
                  zIndex: 60,
                  backgroundColor: theme.branding.gray[0],
                  gap: "8px",
                  justifyContent: "space-between",
                  alignItems: isXsMobile ? "flex-start" : "center",
                  padding: "16px 0",
                  borderBottom: isXsMobile
                    ? "none"
                    : `1px solid ${theme.branding.gray[200]}`,
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    backgroundColor: theme.branding.darkBlue[800],
                  }),
              ]}
            >
              <Grid container direction="column" sx={{ gap: "8px" }}>
                <Typography variant="h2">Games</Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ color: theme.branding.gray[500] }}
                >
                  World Cup 2026 ·{" "}
                  {contestType === "Knockout" ? "Knockout" : "Group Stage"}
                </Typography>
              </Grid>
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={{
                  gap: "8px",
                  alignItems: "flex-start",
                  width: isXsMobile ? "100%" : "auto",
                }}
              >
                <ButtonBase
                  onClick={() => setShowPicker(!showPicker)}
                  id="calendar-icon-button"
                  sx={[
                    {
                      padding: "8px 16px",
                      borderRadius: "10px",
                      border: `1px solid ${theme.branding.gray[300]}`,
                      position: "relative",
                      justifyContent: "center",
                      alignItems: "center",
                      minWidth: "95px",
                      width: isMobile ? "100%" : "auto",
                      "&:hover": {
                        borderColor: theme.branding.cyan[300],
                      },
                    },
                    (theme) =>
                      theme.applyStyles({
                        borderColor: theme.branding.darkBlue[600],
                        backgroundColor: theme.branding.darkBlue[800],
                      }),
                  ]}
                >
                  <Grid
                    container
                    direction="column"
                    sx={{
                      gap: "4px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.branding.gray[500],
                        textTransform: "uppercase",
                      }}
                    >
                      {selectedDateProps?.weekday}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "24px",
                        color: theme.branding.cyan[500],
                      }}
                    >
                      {selectedDateProps?.date}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: theme.branding.gray[500],
                      }}
                    >
                      {selectedDateProps?.gamesQty > 0
                        ? `${selectedDateProps.gamesQty} games`
                        : "Off day"}
                    </Typography>
                  </Grid>
                </ButtonBase>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  dateLibInstance={moment}
                >
                  <DesktopDatePicker
                    open={showPicker}
                    value={selectedDate}
                    onChange={(newValue) => setSelectedDate(newValue)}
                    onClose={() => setShowPicker(false)}
                    timezone={timezone}
                    slots={{ day: GameDay }}
                    slotProps={{
                      popper: {
                        anchorEl: document.getElementById(
                          "calendar-icon-button",
                        ), // Anchor the position to be at the Icon
                        placement: "bottom-start",
                      },
                      textField: {
                        sx: { display: "none" }, // Hide the input field
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            {/* calendar nav */}
            {/* <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={[
              {
                position: "relative",
                width: "100%",
                minWidth: "0",
                padding: "12px 0px",
                backgroundColor: theme.branding.gray[0],
                borderBottom: `1px solid ${theme.branding.gray[200]}`,
                boxShadow: `0 8px 20px -12px ${theme.branding.gray[200]}, 0 3px 5px -4px ${theme.branding.gray[300]}`,
                overflow: "hidden",
                scrollbarWidth: "none",
              },
              (theme) =>
                theme.applyStyles("dark", {
                  backgroundColor: theme.branding.darkBlue[800],
                  borderColor: theme.branding.darkBlue[600],
                }),
            ]}
          >
            <Grid
              container
              direction="row"
              wrap="nowrap"
              sx={{
                width: "100%",
                overflow: "scroll",
                whiteSpace: "nowrap",
                scrollbarWidth: "none",
                gap: "12px",
              }}
            >
              {datesArr.map((date, index) => {
                const isDatePast = date.nextDateStart < currDate;
                const thisDateSelected =
                  date?.dateStart < selectedDate < date?.nextDateStart;

                return (
                  <ButtonBase
                    key={`group-nav-${index}`}
                    onClick={() => {
                      setSelectedDate(date.timestamp);
                    }}
                    disableRipple
                    disabled={isDatePast}
                    sx={{
                      padding: "8px 16px",
                      borderRadius: "10px",
                      border: `${thisDateSelected ? "2px" : "1px"} solid ${isDatePast ? theme.branding.gray[600] : thisDateSelected ? theme.branding.cyan[300] : theme.branding.gray[300]}`,
                      transition: "all 50ms ease-in-out",
                      position: "relative",
                      "&:hover": {
                        borderColor: theme.branding.cyan[300],
                      },
                    }}
                  >
                    <Grid
                      container
                      direction="column"
                      sx={{
                        padding: "10px 20px",
                        gap: "4px",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDatePast
                            ? theme.branding.gray[600]
                            : theme.branding.gray[500],
                        }}
                      >
                        {date.weekday}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontSize: "24px",
                          color: isDatePast
                            ? theme.branding.gray[600]
                            : "inherit",
                        }}
                      >
                        {date.date}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: isDatePast
                            ? theme.branding.gray[600]
                            : theme.branding.gray[500],
                        }}
                      >
                        {date.gamesQty > 0
                          ? `${date.gamesQty} games`
                          : "Off day"}
                      </Typography>
                    </Grid>
                     //placeholder grid for transparent background 
                    {thisDateSelected && (
                      <Grid
                        sx={{
                          height: "100%",
                          width: "100%",
                          borderRadius: "10px",
                          backgroundColor: theme.branding.cyan[500],
                          opacity: "10%",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    )}
                  </ButtonBase>
                );
              })}
            </Grid>
          </Grid> */}
            <Grid
              container
              direction="column"
              sx={{ gap: "12px", justifyContent: "center" }}
            >
              {_.isEmpty(selectedDateGames) ? (
                <Grid
                  container
                  sx={[
                    {
                      borderRadius: "14px",
                      width: isMobile ? "100%" : "calc(100% - 48px)",
                      padding: isMobile ? "32px 16px" : "120px 16px",
                      backgroundColor: theme.branding.gray[0],
                      border: `1px solid ${theme.branding.gray[200]}`,
                      justifyContent: "center",
                      alignItems: "center",
                      alignSelf: "center",
                    },
                    (theme) =>
                      theme.applyStyles({
                        borderColor: theme.branding.darkBlue[600],
                        backgroundColor: theme.branding.darkBlue[800],
                      }),
                  ]}
                >
                  <Grid
                    container
                    direction="row"
                    wrap="nowrap"
                    sx={{
                      width: "100%",
                      height: "fit-content",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Grid
                      sx={{
                        width: "100%",
                        maxWidth: "200px",
                        height: "2px",
                        borderRadius: "10px",
                        backgroundColor: theme.branding.gray[200],
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      sx={{ color: theme.branding.gray[400] }}
                    >
                      OFF DAY
                    </Typography>
                    <Grid
                      sx={{
                        width: "100%",
                        maxWidth: "200px",
                        height: "2px",
                        borderRadius: "10px",
                        backgroundColor: theme.branding.gray[200],
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  container
                  direction="column"
                  sx={{ gap: "24px", justifyContent: "center" }}
                >
                  {selectedDateGames.map((game, index) => (
                    <Grid
                      key={`game-${index}`}
                      container
                      direction="column"
                      sx={[
                        {
                          position: "relative",
                          zIndex: 2,
                          padding: "24px",
                          gap: "12px",
                          width: "100%",
                          borderRadius: "14px",
                          border: `1px solid ${
                            game?.game_status === "InProgress"
                              ? "#FB2C3633" //20% opacity red[500]
                              : theme.branding.gray[200]
                          }`,
                          backgroundColor: theme.branding.gray[0],
                        },
                        (theme) =>
                          theme.applyStyles("dark", {
                            border: `1px solid ${
                              game?.game_status === "InProgress"
                                ? "#FB2C361A" //10% opacity red[500]
                                : theme.branding.darkBlue[600]
                            }`,
                            backgroundColor: theme.branding.darkBlue[800],
                          }),
                      ]}
                    >
                      {/* chips and live */}
                      <Grid
                        container
                        direction="row"
                        wrap="nowrap"
                        sx={{
                          justifyContent: "space-between",
                        }}
                      >
                        <Grid container direction="row" sx={{ gap: "8px" }}>
                          <Grid container direction="column" sx={{ gap: "2px" }}>
                            <Grid
                              container
                              sx={{
                                width: "fit-content",
                                backgroundColor: game?.is_reference
                                  ? theme.branding.gray[200]
                                  : "#33DDEE1A",
                                borderRadius: "8px",
                                padding: "4px 8px",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontSize: "12px",
                                  color: game?.is_reference
                                    ? theme.branding.gray[500]
                                    : theme.branding.cyan[500],
                                }}
                              >
                                {game?.is_reference
                                  ? `World Cup · Group ${game?.group}`
                                  : game?.wc_stage === "knockout"
                                    ? game?.round_name || "Knockout"
                                    : `Group ${game?.group}`}
                              </Typography>
                            </Grid>
                            {game?.is_reference && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "10px",
                                  color: theme.branding.gray[400],
                                }}
                              >
                                Group stage · not scored in this contest
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                        {game?.game_status === "InProgress" && (
                          <Grid
                            container
                            direction="row"
                            spacing="4px"
                            wrap="nowrap"
                            sx={{
                              backgroundColor: theme.branding.red[500],
                              padding: "4px 8px",
                              borderRadius: "16px",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CircleIcon
                              htmlColor={theme.branding.gray[0]}
                              sx={{
                                color: theme.branding.gray[0],
                                height: "6px",
                                width: "6px",
                                animation: "fading 4500ms infinite",
                                "@keyframes fading": {
                                  "0%": {
                                    opacity: 0.25,
                                  },
                                  "10%": { opacity: 0.3 },
                                  "40%": { opacity: 0.75 },
                                  "50%": { opacity: 0.8 },
                                  "60%": { opacity: 0.75 },
                                  "90%": { opacity: 0.3 },
                                  "100%": {
                                    opacity: 0.25,
                                  },
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.branding.gray[0],
                                fontSize: "10px",
                                fontWeight: 700,
                                alignSelf: "center",
                              }}
                            >
                              LIVE
                            </Typography>
                          </Grid>
                        )}
                      </Grid>

                      {/* time */}
                      {game?.game_status === "Scheduled" && (
                        <Grid
                          container
                          sx={{
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontSize: "24px",
                              textTransform: "uppercase",
                            }}
                          >
                            {getScheduledTime(game)}
                          </Typography>
                        </Grid>
                      )}

                      {/* teams */}
                      <Grid container direction="column" sx={{ gap: "8px" }}>
                        {/* team 1 */}
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            sx={[
                              {
                                position: "relative",
                                alignItems: "center",
                                gap: "12px",
                              },
                            ]}
                          >
                            <Grid
                              container
                              sx={{
                                width: "48px",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {game?.team_1?.country_name ? (
                                <img
                                  src={game?.team_1?.flag_image_url}
                                  alt={`${game?.team_1?.country_name}-country-flag-icon`}
                                  style={{
                                    maxWidth: "48px",
                                    maxHeight: "24px",
                                  }}
                                />
                              ) : (
                                <Grid
                                  sx={{
                                    width: "48px",
                                    height: "24px",
                                    borderRadius: "4px",
                                    backgroundColor: theme.branding.gray[200],
                                  }}
                                />
                              )}
                            </Grid>
                            <Grid container direction="column">
                              <Grid
                                container
                                direction="row"
                                wrap="nowrap"
                                sx={{
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <Typography variant="subtitle1">
                                  {game?.team_1?.country_name || "TBD"}
                                </Typography>
                                {game?.team_1?.country_name && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.branding.gray[400],
                                      fontWeight: 500,
                                    }}
                                  >
                                    #{game?.team_1?.world_rank}
                                  </Typography>
                                )}
                              </Grid>
                              <Typography variant="caption">
                                {game?.team_1?.country_name
                                  ? getCountryInfo(game?.team_1?.country_name)
                                      ?.code
                                  : ""}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            sx={{
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                color:
                                  game?.game_status !== "Scheduled" &&
                                  Number(game?.team_1?.goals) >
                                    Number(game?.team_2?.goals)
                                    ? theme.branding.cyan[500]
                                    : theme.branding.gray[500],
                              }}
                            >
                              {game?.game_status === "Scheduled"
                                ? ""
                                : game?.team_1?.goals}
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* divider — VS sits between the two team names (left,
                            indented past the flag) and is tinted blue */}
                        <Grid
                          container
                          sx={{
                            justifyContent: "flex-start",
                            alignItems: "center",
                            paddingLeft: "60px",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.branding.cyan[500],
                              fontWeight: 700,
                              textTransform: "uppercase",
                            }}
                          >
                            {game?.game_status !== "Scheduled" ? "—" : "vs"}
                          </Typography>
                        </Grid>

                        {/* team 2 */}
                        <Grid
                          container
                          direction="row"
                          wrap="nowrap"
                          sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            sx={[
                              {
                                position: "relative",
                                alignItems: "center",
                                gap: "12px",
                              },
                            ]}
                          >
                            <Grid
                              container
                              sx={{
                                width: "48px",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {game?.team_2?.country_name ? (
                                <img
                                  src={game?.team_2?.flag_image_url}
                                  alt={`${game?.team_2?.country_name}-country-flag-icon`}
                                  style={{
                                    maxWidth: "48px",
                                    maxHeight: "24px",
                                  }}
                                />
                              ) : (
                                <Grid
                                  sx={{
                                    width: "48px",
                                    height: "24px",
                                    borderRadius: "4px",
                                    backgroundColor: theme.branding.gray[200],
                                  }}
                                />
                              )}
                            </Grid>
                            <Grid container direction="column">
                              <Grid
                                container
                                direction="row"
                                wrap="nowrap"
                                sx={{
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <Typography variant="subtitle1">
                                  {game?.team_2?.country_name || "TBD"}
                                </Typography>
                                {game?.team_2?.country_name && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.branding.gray[400],
                                      fontWeight: 500,
                                    }}
                                  >
                                    #{game?.team_2?.world_rank}
                                  </Typography>
                                )}
                              </Grid>
                              <Typography variant="caption">
                                {game?.team_2?.country_name
                                  ? getCountryInfo(game?.team_2?.country_name)
                                      ?.code
                                  : ""}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            sx={{
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="h3"
                              sx={{
                                color:
                                  game?.game_status !== "Scheduled" &&
                                  Number(game?.team_2?.goals) >
                                    Number(game?.team_1?.goals)
                                    ? theme.branding.cyan[500]
                                    : theme.branding.gray[500],
                              }}
                            >
                              {game?.game_status === "Scheduled"
                                ? ""
                                : game?.team_2?.goals}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </ContentWrapper>
      )}
    </>
  );
};

export default Games;
